import { useState, useEffect } from 'react';
import { Box, Stack, Paper, Typography, List, ListItem, ListItemText } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import PatientDatas from "src/modules/medcard/patient-datas";
import ByzMuiAddButton from 'src/components/core-components/mui/buttons/ByzMuiAddButton';
import ByzMuiPrintButton from 'src/components/core-components/mui/buttons/ByzMuiPrintButton';
import { useBaseQuery } from 'src/api/core/useBaseQuery';
import LiquidInTable from '../components/LiquidInTable';
import LiquidOutTable from '../components/LiquidOutTable';
import FluidBalanceGrid from '../components/FluidBalanceGrid';
import OpenComponentDialog from 'src/components/shared-components/modal/openComponentModal';
import LiquidInModal from '../components/LiquidInModal';
import LiquidOutModal from '../components/LiquidOutModal';
import { useAppSelector } from 'src/app/hooks';

export default function FluidBalanceCreate(props: any) {
  const { getComId, ptId, orId } = props;
  const mainTabs = useAppSelector((state) => state.tabs.mainTabs);
  const activeTabIndex = useAppSelector((state) => state.tabs.activeTabIndex);
  const selectedPatientId = mainTabs[activeTabIndex]?.data?.patientId;

  const [comId, setcomId] = useState(0)
  const [liquidInModalOpen, setLiquidInModalOpen] = useState(false);
  const [liquidOutModalOpen, setLiquidOutModalOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState<string>('');
  const [fbid, setFbid] = useState<number | null>(null);

  useEffect(() => {
    if (ptId && orId) {
      getComId()
        .then((id: any) => {
          if (id) setcomId(id);
        });
    }
  }, [getComId, ptId, orId]);

  const { data: fluidTypes, isLoading } = useBaseQuery<any>({
    endpoint: `/reference/api/Card/GetPatientFluidBalance?comId=${comId}`,
    queryKey: ['fluidTypesBalance', comId],
    options: {
      enabled: comId > 0,
    }
  });


  // Seçili tarihe göre ilk tarihi set et
  useEffect(() => {
    if (fluidTypes?.data && fluidTypes.data.length > 0 && !selectedDate) {
      setSelectedDate(fluidTypes.data[0].pdate);
    }
  }, [fluidTypes?.data, selectedDate]);

  // Seçili tarihe ait datayı bul
  const selectedDateData = fluidTypes?.data?.find((item: any) => item.pdate === selectedDate) || fluidTypes?.data?.[0];

  // Tüm günlerin toplamını hesapla
  const totalIn = fluidTypes?.data?.reduce((sum: number, item: any) => sum + (item.day_total_in || 0), 0) || 0;
  const totalOut = fluidTypes?.data?.reduce((sum: number, item: any) => sum + (item.day_total_out || 0), 0) || 0;
  const totalBalance = totalIn - totalOut;

  // Toplam Sıvı için genel toplam veri
  const totalFluid = [
    { label: 'Toplam Alınan', value: `${totalIn} ml` },
    { label: 'Toplam Çıkan', value: `${totalOut} ml` },
    { label: 'Denge', value: `${totalBalance} ml` },
  ];



  return (
    <>
      <PatientDatas isCreateMedCard isOpenMedCard />

      <Box sx={{ p: 3 }}>

        <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
          <ByzMuiPrintButton type={1} />
          <ByzMuiAddButton onClick={() => setLiquidInModalOpen(true)}>
            Sıvı Ekle
          </ByzMuiAddButton>
          <ByzMuiAddButton onClick={() => setLiquidOutModalOpen(true)}>
            Çıkış Ekle
          </ByzMuiAddButton>
        </Stack>


        <Grid container spacing={2}>
          <Grid xs={12} md={6}>
            <LiquidInTable
              comId={comId}
              setFbid={setFbid}
            />
          </Grid>

          <Grid xs={12} md={6}>
            <LiquidOutTable
              comId={comId}
            />
          </Grid>
        </Grid>

        <Grid container spacing={2} sx={{ mt: 2 }}>
          <Grid xs={12} md={2}>
            <Paper sx={{ p: 2, borderRadius: '16px' }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Tarihler
              </Typography>
              <List dense>
                {fluidTypes?.data?.map((item: any, index: number) => (
                  <ListItem
                    key={index}
                    component="div"
                    selected={selectedDate === item.pdate}
                    onClick={() => setSelectedDate(item.pdate)}
                    sx={{
                      borderRadius: '8px',
                      mb: 1,
                      cursor: 'pointer',
                      '&.Mui-selected': {
                        backgroundColor: 'primary.lighter',
                        '&:hover': {
                          backgroundColor: 'primary.light',
                        },
                      },
                      '&:hover': {
                        backgroundColor: 'action.hover',
                      },
                    }}
                  >
                    <ListItemText
                      primary={new Date(item.pdate).toLocaleDateString('tr-TR', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric'
                      })}
                      primaryTypographyProps={{
                        fontWeight: selectedDate === item.pdate ? 600 : 400
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>

          <Grid xs={12} md={8}>
            <FluidBalanceGrid
              selectedDateData={selectedDateData}
              isLoading={isLoading}
            />
          </Grid>

          <Grid xs={12} md={2}>
            <Paper sx={{ p: 2, borderRadius: '16px' }}>
              <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                Toplam Sıvı
              </Typography>
              <List dense>
                {totalFluid.map((item, index) => (
                  <ListItem key={index}>
                    <ListItemText
                      primary={item.label}
                      secondary={item.value}
                      primaryTypographyProps={{ fontWeight: 500 }}
                      secondaryTypographyProps={{
                        sx: { fontSize: '1.1rem', color: 'primary.main', fontWeight: 600 }
                      }}
                    />
                  </ListItem>
                ))}
              </List>
            </Paper>
          </Grid>
        </Grid>
      </Box>

      <OpenComponentDialog
        showPatientData={false}
        title="Sıvı Girişi"
        open={liquidInModalOpen}
        onClose={() => setLiquidInModalOpen(false)}
        maxWidth="sm"
      >
        <LiquidInModal
          comId={comId}
          ptId={selectedPatientId}
          onClose={() => setLiquidInModalOpen(false)}
        />
      </OpenComponentDialog>

      <OpenComponentDialog
        showPatientData={false}
        title="Sıvı Çıkışı"
        open={liquidOutModalOpen}
        onClose={() => setLiquidOutModalOpen(false)}
        maxWidth="sm"
      >
        <LiquidOutModal
          fbid={fbid!}
          ptId={selectedPatientId}
          onClose={() => setLiquidOutModalOpen(false)}
        />
      </OpenComponentDialog>
    </>
  );
}
